/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  InputHTMLAttributes,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react'

interface Props extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  name: string
  size: number
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  clearError?: () => void
}

const InputSMSCode: React.FC<Props> = ({
  name,
  size,
  value,
  onChange,
  onComplete,
  clearError,
  ...rest
}) => {
  const arr = useMemo(() => Array.from({ length: size }, (_, i) => i), [size])

  const [isFocused, setIsFocused] = useState(false)
  const [, setIsFilled] = useState(false)

  const arrValues = useMemo(() => (value ?? '').split(''), [value])

  const selectedIndex = useMemo(
    () => (arrValues.length < size ? arrValues.length : size - 1),
    [arrValues.length, size],
  )

  const hideInput = useMemo(
    () => !(arrValues.length < size),
    [arrValues.length, size],
  )

  const inputRefTmp = useRef<HTMLInputElement>(null)
  const inputLeft = selectedIndex * 56

  const handleClick = useCallback(() => {
    inputRefTmp.current?.focus()
  }, [])

  const handleInputFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleInputBlur = useCallback(() => {
    setIsFocused(false)
    setIsFilled(!!inputRefTmp.current?.value)
  }, [])

  const appendDigit = useCallback(
    (digit: string) => {
      if (!digit) return

      const d = digit.replace(/\D/g, '').slice(0, 1)
      if (!d) return

      if ((value ?? '').length < size) {
        const next = `${value ?? ''}${d}`
        onChange(next)

        if (clearError) clearError()
      }
    },
    [clearError, onChange, size, value],
  )

  const handleChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      appendDigit(event.currentTarget.value)

      if (inputRefTmp.current) inputRefTmp.current.value = ''
    },
    [appendDigit],
  )

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Backspace') {
        onChange((value ?? '').slice(0, (value ?? '').length - 1))
        if (clearError) clearError()
      }
    },
    [clearError, onChange, value],
  )

  const lastCompletedRef = useRef<string>('')

  useEffect(() => {
    const code = value ?? ''

    if (code.length !== size) {
      lastCompletedRef.current = ''
      return
    }

    if (lastCompletedRef.current === code) return

    lastCompletedRef.current = code
    onComplete?.(code)
  }, [onComplete, size, value])

  return (
    <div>
      <div
        onClick={handleClick}
        className="relative flex cursor-default border-0 bg-transparent"
      >
        <input type="hidden" name={name} value={value} />

        <input
          type="tel"
          inputMode="numeric"
          autoComplete="one-time-code"
          value=""
          onChange={handleChange}
          onKeyUp={handleKeyUp}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          ref={inputRefTmp}
          style={{ left: `${inputLeft}px` }}
          className={`absolute top-0 bottom-0 h-[32px] w-[40px] cursor-text border-0 bg-transparent p-[8px] text-center outline-none ${
            hideInput ? 'opacity-0' : 'opacity-100'
          } appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
          {...rest}
        />

        {arr.map((_, index) => {
          const indexFocused = isFocused && arrValues.length === index
          const indexFilled = arrValues.length > index

          const cellClassName = [
            'relative flex h-[32px] w-[40px] items-center justify-center border-b-[2px] border-[#d2d2d2] p-[8px] text-[20px] font-bold text-[#555555]',
            index > 0 ? 'ml-[16px]' : '',
            indexFilled ? 'border-[#B5BEF8]' : '',
            indexFocused ? 'border-[#8493F4]' : '',
          ]
            .filter(Boolean)
            .join(' ')

          return (
            <div className={cellClassName} key={index}>
              {arrValues[index]}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default InputSMSCode
